import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { find, identity } from 'lodash/fp';

import ImageWithFrame from '../ui/ImageWithFrame';
import ColorProvider from '../chrome/ColorProvider';
import LoadingIcon from '../chrome/LoadingIcon';
import Spacing from '../layout/Spacing';
import Text from '../chrome/Text';
import Image from '../chrome/Image';
import CheckCircle from '../chrome/CheckCircle';
import Radio from '../chrome/Radio';
import { useCdnImage } from '../../app/context/config';

const containerBaseStyles = ({ theme }) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacings.kilo};
`;

const containerPaddingStyles = ({ theme, reducePadding }) =>
  reducePadding &&
  css`
    padding-left: ${theme.spacings.bit};
    padding-right: ${theme.spacings.bit};
  `;

const containerClickableStyles = ({ isClickable }) =>
  isClickable &&
  css`
    cursor: pointer;
  `;

const containerActiveStyles = ({ theme, isActive }) =>
  isActive &&
  css`
    background-color: ${theme.colors.g300};
  `;

const Container = styled('div')(
  containerBaseStyles,
  containerPaddingStyles,
  containerClickableStyles,
  containerActiveStyles,
);

const imageContainerBaseStyles = () => css`
  position: relative;
`;

const ImageContainer = styled('div')(imageContainerBaseStyles);

const secondaryImageContainerBaseStyles = () => css`
  position: absolute;
  bottom: 0;
  right: -10px;
`;

const SecondaryImageContainer = styled('div')(secondaryImageContainerBaseStyles);

const rowBaseStyles = () => css`
  display: flex;
  align-items: center;
`;

const rowVariantStyles = ({ variant }) =>
  variant === 'centered' &&
  css`
    justify-content: center;
  `;

const Row = styled('div')(rowBaseStyles, rowVariantStyles);

const textPlaceholderBaseStyles = ({ theme, length, height }) =>
  css`
    background-color: ${theme.colors.n500};
    width: ${length}px;
    height: ${height}px;
    margin-bottom: ${theme.spacings.bit};

    &:last-of-type {
      margin-bottom: 0;
    }
  `;

const TextPlaceholder = styled('div')(textPlaceholderBaseStyles);

const iconContainerBaseStyles = () =>
  css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    height: 20px;
  `;

const iconContainerLeftStyles = ({ variant }) =>
  variant === 'left' &&
  css`
    margin-left: auto;
  `;

const IconContainer = styled('div')(iconContainerBaseStyles, iconContainerLeftStyles);

const subTextProps = {
  centered: {
    size: 'kilo',
    bold: true,
    upper: false,
    noMargin: true,
  },
  left: {
    size: 'tera',
    bold: false,
    upper: false,
    noMargin: true,
  },
  leftSelected: {
    size: 'tera',
    bold: true,
    upper: false,
    noMargin: true,
  },
  placeholder: {
    size: 'tera',
    bold: false,
    upper: false,
    noMargin: true,
  },
};

const subTextColor = {
  centered: 'grey',
  left: 'black',
  leftSelected: 'black',
  placeholder: 'darkGrey',
};

const textProps = {
  centered: {
    size: 'tera',
    bold: false,
    upper: false,
    noMargin: true,
  },
  left: {
    size: 'kilo',
    bold: false,
    upper: false,
    noMargin: true,
  },
  leftSelected: {
    size: 'kilo',
    bold: true,
    upper: false,
    noMargin: true,
  },
};

const textColor = {
  centered: 'black',
  left: 'grey',
  leftSelected: 'grey',
};

const StyledIcon = ({ variant = 'chevron-down' }) => {
  const cdnImages = useCdnImage(['chevronUp', 'chevronDown', 'iconLockNew', 'iconFlame']);

  const icons = [
    variant === 'success' && <CheckCircle />,
    variant === 'success-lite' && <CheckCircle variant='lite' />,
    variant === 'chevron-down' && <Image src={cdnImages.chevronDown} alt='chev_down' isSvg />,
    variant === 'chevron-up' && <Image src={cdnImages.chevronUp} alt='chev_up' isSvg />,
    variant === 'lock' && <Image src={cdnImages.iconLockNew} alt='lock' isSvg />,
    variant === 'radio' && <Radio />,
    variant === 'radioActive' && <Radio isActive />,
  ];

  return find(identity, icons) || <Image src={cdnImages.chevronDown} alt='chev_down' isSvg />;
};

const TextLine = ({ rowVariant, colorVariant, textOptions, text }) => (
  <Row variant={rowVariant}>
    <ColorProvider variant={colorVariant}>
      <Text as='span' {...textOptions}>
        {text}
      </Text>
    </ColorProvider>
  </Row>
);

const getIcon = ({ hasIcon, variant, icon }) =>
  hasIcon ? (
    <IconContainer variant={variant}>
      <StyledIcon variant={icon || 'chevron-down'} />
    </IconContainer>
  ) : null;

const PickItem = ({
  isPlaceholder,
  variant,
  isClickable,
  assetUrl,
  assetSize,
  secondaryAssetUrl,
  secondaryAssetSize,
  assetNoBorder,
  borderColor,
  subText,
  text,
  icon,
  isActive,
  hasIcon,
  onSelect,
  reducePadding,
}) => {
  const showPrimaryAsset = assetUrl || isPlaceholder;

  return (
    <Container onClick={onSelect} isClickable={isClickable} isActive={isActive} reducePadding={reducePadding}>
      <ImageContainer>
        {showPrimaryAsset && (
          <ImageWithFrame
            assetUrl={assetUrl}
            noBorder={assetNoBorder}
            hasPadding={false}
            borderColor={borderColor}
            size={assetSize}
            variant={isPlaceholder && 'solid'}
          />
        )}
        {secondaryAssetUrl && (
          <SecondaryImageContainer>
            <ImageWithFrame
              assetUrl={secondaryAssetUrl}
              noBorder={assetNoBorder}
              borderColor={borderColor}
              size={secondaryAssetSize}
              variant={isPlaceholder && 'solid'}
            />
          </SecondaryImageContainer>
        )}
      </ImageContainer>
      <Spacing sides double>
        {isPlaceholder ? (
          <>
            <TextPlaceholder length={150} height={20} />
            <TextPlaceholder length={100} height={18} />
          </>
        ) : (
          <>
            {subText && (
              <TextLine
                rowVariant={variant}
                colorVariant={subTextColor[variant]}
                textOptions={subTextProps[variant]}
                text={subText}
              />
            )}
            {text && (
              <TextLine
                rowVariant={variant}
                colorVariant={textColor[variant]}
                textOptions={textProps[variant]}
                text={text}
              />
            )}
          </>
        )}
      </Spacing>
      {isPlaceholder ? <LoadingIcon size={1} variant='secondary' /> : getIcon({ hasIcon, variant, icon })}
    </Container>
  );
};

PickItem.propTypes = {
  variant: PropTypes.string,
  isPlaceholder: PropTypes.bool,
  isClickable: PropTypes.bool,
  rowVariant: PropTypes.string,
  colorVariant: PropTypes.string,
  borderColor: PropTypes.string,
  assetUrl: PropTypes.string,
  assetSize: PropTypes.string,
  secondaryAssetUrl: PropTypes.string,
  secondaryAssetSize: PropTypes.string,
  assetNoBorder: PropTypes.bool,
  subText: PropTypes.string,
  hasIcon: PropTypes.bool,
  text: PropTypes.string,
  icon: PropTypes.string,
  isActive: PropTypes.bool,
  onSelect: PropTypes.func,
};

TextLine.propTypes = {
  text: PropTypes.string,
  size: PropTypes.string,
  rowVariant: PropTypes.string,
  colorVariant: PropTypes.string,
  textOptions: PropTypes.object,
};

PickItem.defaultProps = {
  variant: 'centered',
  assetSize: 'peta',
  secondaryAssetSize: 'bit',
  hasIcon: true,
};

export default PickItem;
